import React from 'react';
import { Tabs } from 'antd';
import { EditOutlined, InfoCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { Banner, isCreativityBanner, isPromptBanner, PromptBanner } from '../../../types/banner';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

import { ConfigurationCard } from './configuration-card';
import { GeneralInfoCard } from './general-info-card';
import { ChatPanel } from '../chat-panel/chat-panel';
import { ReviewPanel } from '../review-panel/review-panel';

interface AssetInfoPanelProps {
  banner: Banner;
  clientId: string;
  onDeleted?: () => void;
  onClose: () => void;
}

export const AssetInfoPanel: React.FC<AssetInfoPanelProps> = ({ banner, clientId, onDeleted, onClose }) => {
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const showChat = isPromptBanner(banner);

  const showReview =
    isCreativityBanner(banner) &&
    banner.reviewFields &&
    Object.values(banner.reviewFields).some(field => field?.status === 'pending');

  const defaultTab = showReview ? 'review' : showChat ? 'chat' : 'info';

  const tabContentHeight = screens.xs
    ? '50vh' // móviles pequeños
    : screens.sm
      ? '55vh' // tablets
      : screens.md
        ? '58vh' // laptops pequeñas
        : '60vh'; // desktop

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: spacing.lg
      }}
    >
      <Tabs
        defaultActiveKey={defaultTab}
        size='small'
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        items={[
          {
            key: 'info',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  fontSize: fontSize.base,
                  fontWeight: 600
                }}
              >
                <InfoCircleOutlined />
                Información
              </div>
            ),
            children: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.lg,
                  height: tabContentHeight,
                  overflowY: 'auto',
                  paddingRight: spacing.sm,
                  scrollbarColor: '#e3e3e3 #FFFFFF'
                }}
              >
                <GeneralInfoCard banner={banner} onDeleted={onDeleted} onClose={onClose} />
                {isCreativityBanner(banner) && <ConfigurationCard banner={banner} clientId={clientId} />}
              </div>
            )
          },
          {
            key: 'chat',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  padding: `2px ${spacing.sm}px`,
                  fontSize: fontSize.base,
                  fontWeight: 600
                }}
              >
                <MessageOutlined />
                Chat
              </div>
            ),
            disabled: !showChat,
            children: showChat ? (
              <div style={{ height: tabContentHeight }}>
                <ChatPanel banner={banner as PromptBanner} />
              </div>
            ) : null
          },

          {
            key: 'review',
            label: (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '2px 15px',
                  fontSize: fontSize.base,
                  fontWeight: 600
                }}
              >
                <EditOutlined />
                Revisión
                {showReview && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      background: '#f59e0b',
                      borderRadius: '50%',
                      marginLeft: 4
                    }}
                  />
                )}
              </div>
            ),
            disabled: !showReview,
            children: showReview ? (
              <div
                style={{
                  height: tabContentHeight,
                  overflowY: 'auto',
                  paddingRight: spacing.sm,
                  scrollbarColor: '#e3e3e3 #FFFFFF'
                }}
              >
                <ReviewPanel banner={banner} />
              </div>
            ) : null
          }
        ]}
      />
    </div>
  );
};
